import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { wv, BtnSecondary } from '../login/LoginScreen';
import { webvonkClient } from '../lib/supabase';
import WebvonkLogoImage from '../assets/Webvonk.png';
import { getFiniziSession, clearFiniziSession } from './finiziSession';

// ─── Styles ───────────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  background: ${wv.bg};
  display: flex;
  flex-direction: column;
  font-family: ${wv.fontSans};
  -webkit-font-smoothing: antialiased;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: clamp(20px, 3vh, 36px) ${wv.px};
`;

const HeaderLogo = styled.img`
  height: 40px;
  width: auto;
  object-fit: contain;
`;

const Main = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: clamp(16px, 2.5vh, 28px);
  padding: 32px ${wv.px};
`;

const LogoTile = styled.div`
  width: 120px;
  height: 120px;
  background: #ffffff;
  border: 1px solid ${wv.lines};
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(12,12,11,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  box-sizing: border-box;
  animation: ${fadeUp} 0.5s ease both;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const Label = styled.span`
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: ${wv.accentSmall};
  animation: ${fadeUp} 0.5s ease 0.1s both;
`;

const Heading = styled.h1`
  font-family: 'Playfair Display', Georgia, serif !important;
  font-size: clamp(32px, 5vw, 64px);
  font-weight: 900 !important;
  font-synthesis: none;
  line-height: 0.95;
  letter-spacing: -0.02em;
  color: ${wv.text};
  margin: 0;
  animation: ${fadeUp} 0.55s ease 0.2s both;

  em {
    font-family: 'Playfair Display', Georgia, serif !important;
    font-style: italic;
    color: ${wv.accent};
  }
`;

const Subtitle = styled.p`
  font-size: clamp(14px, 1.1vw, 16px);
  color: ${wv.body};
  line-height: 1.6;
  margin: 0;
  max-width: 42ch;
  animation: ${fadeUp} 0.5s ease 0.3s both;
`;

const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 0;
  border-top: 1px solid ${wv.lines};
  margin: 0 ${wv.px};
`;

const FooterText = styled.span`
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${wv.body};
`;

// ─── Component ────────────────────────────────────────────────────────────────

function ComingSoon({ onLogout }) {
  return (
    <Page>
      <Header>
        <HeaderLogo src={WebvonkLogoImage} alt="Webvonk" />
        <BtnSecondary onClick={onLogout}>Uitloggen</BtnSecondary>
      </Header>

      <Main>
        <LogoTile>
          <img src={`${process.env.PUBLIC_URL}/assets/Finizi_logo.png`} alt="Finizi" />
        </LogoTile>
        <Label>Finizi</Label>
        <Heading>Binnenkort <em>beschikbaar</em>.</Heading>
        <Subtitle>
          We zijn jouw omgeving aan het bouwen.
        </Subtitle>
      </Main>

      <Footer>
        <FooterText>© {new Date().getFullYear()} Webvonk</FooterText>
        <FooterText>Laat jouw merk online vonken.</FooterText>
      </Footer>
    </Page>
  );
}

export default function FiniziApp() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    setUser(getFiniziSession());
    setChecked(true);
  }, []);

  const handleLogout = async () => {
    clearFiniziSession();
    try { await webvonkClient.auth.signOut(); } catch {}
    setUser(null);
    navigate('/', { replace: true });
  };

  if (!checked) return null;

  return (
    <Routes>
      <Route path="binnenkort" element={user ? <ComingSoon onLogout={handleLogout} /> : <Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to={user ? '/Finizi/binnenkort' : '/'} replace />} />
    </Routes>
  );
}
